'use client';
import { DependencyList, useCallback, useState } from 'react';

export type AsyncFunction<A extends unknown[], O> = (...args: A) => Promise<O>;
export function useSimpleMutation<A extends unknown[], O, ErrorType = unknown>(
  callback: AsyncFunction<A, O>,
  deps: DependencyList
): {
  isPending: boolean;
  submitted: boolean;
  mutate: AsyncFunction<A, O>;
  error?: ErrorType | null;
  resetError: () => void;
} {
  const [isPending, setIsPending] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState<ErrorType | null>(null);
  const resetError = useCallback(() => setError(null), []);
  const mutate = useCallback(async (...args: A) => {
    setIsPending(true);
    setSubmitted(true);
    try {
      const res = await callback(...args);
      return res;
    } catch (err: unknown) {
      console.error('Error', err);
      setError(err as ErrorType);
      setSubmitted(false);
      throw err;
    } finally {
      setIsPending(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return {
    isPending,
    submitted,
    mutate,
    error,
    resetError,
  };
}
